import { IFRAME_UNAVAILABLE_MESSAGE, isRenderableIframeSource } from './iframeSource';
import type { HeadingLevel } from './TextUnit';
import type { IframeUnitData } from './useComponent';
import { CaptionText } from './CaptionText';
import styles from './IframeUnit.module.css';

interface IframeUnitProps {
  headingLevel?: HeadingLevel;
  hideHeading?: boolean;
  unit: IframeUnitData;
}

function clampHeadingLevel(level: HeadingLevel | undefined): HeadingLevel {
  if (!level) {
    return 2;
  }

  return Math.min(4, Math.max(2, level)) as HeadingLevel;
}

export function IframeUnit({
  headingLevel,
  hideHeading = false,
  unit,
}: IframeUnitProps) {
  const HeadingTag = `h${clampHeadingLevel(headingLevel)}` as const;
  const visibleHeading = hideHeading
    ? null
    : unit.numberedHeading ?? unit.heading ?? null;
  const canRender = isRenderableIframeSource(unit.src);

  return (
    <article className={styles.unit}>
      {visibleHeading ? (
        <HeadingTag className={styles.heading} id={unit.anchorId}>
          {visibleHeading}
        </HeadingTag>
      ) : null}

      {canRender ? (
        <div className={styles.frameWrapper}>
          <iframe
            className={styles.frame}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-presentation"
            src={unit.src}
            style={{
              height: `${unit.height}px`,
              width: `${unit.widthPercent}%`,
            }}
            title={unit.title}
          />
        </div>
      ) : (
        <p className={styles.fallback} role="note">
          {IFRAME_UNAVAILABLE_MESSAGE}
        </p>
      )}

      {unit.caption ? (
        <p className={styles.caption}>
          <CaptionText text={unit.caption} />
        </p>
      ) : null}
    </article>
  );
}
